/**
 * CANOA — Slide Reveal block
 *
 * A trigger button sits on a slide; clicking it reveals a full-slide frosted
 * (backdrop-filter) layer holding editor-authored content, with a mask-reveal
 * entrance (aperture / wipe / slide-in, CSS-driven). Closes via the × button,
 * the Escape key, or the slide scrolling out of view — reversing the same
 * animation.
 *
 * Two structural tricks (see DECISIONS D30 / CUSTOMIZATIONS §14):
 *  1. Portal: on connect we move ourselves to be a direct child of
 *     <slideshow-slide>. This escapes `.slide__content`'s scroll-timeline
 *     opacity group (so the trigger never fades) and anchors our absolute
 *     positioning to the whole slide so the blur samples the media cleanly.
 *  2. stopPropagation on our clicks so the slide's `on:click="/select/i"`
 *     navigation never fires while the visitor is using the panel.
 */

const TRIGGER = '[data-sr-trigger]';
const CLOSE = '[data-sr-close]';
// Hover-intent timings (desktop hover trigger): open after a brief intent delay
// so a cursor merely passing over the slide doesn't fire it; close after a
// slightly longer grace so a quick exit (or dipping to the slide edge) doesn't
// flicker. Tunable — could be exposed as editor settings later.
const HOVER_OPEN_DELAY = 100; // ms
const HOVER_CLOSE_DELAY = 150; // ms

// ── D77 — scroll-stop gating for the desktop hover trigger ─────────────────
// Chrome re-evaluates hover whenever a scroll settles, so slides scrolling
// under a PARKED cursor fire mouseenter/mouseleave as if the visitor were
// hovering them on purpose. With stacked hover-mode slideshows that produced a
// blink chain while scrolling: every section's blur opened 100ms after sliding
// under the cursor and was reverse-faded 90ms after the next one arrived —
// no fade ever completed. The gate below makes scrolling inert: enters that
// arrive while the page (or the carousel) is scrolling are ignored, and when
// scrolling settles each instance checks — via tracked pointer coordinates +
// elementFromPoint, NOT Chrome's flaky synthetic hover events — whether the
// cursor is resting on its slide, and opens calmly if so. Closing is
// deliberately NOT gated: with no competing bloom a close mid-scroll is a
// single clean fade-out ("I'm moving on"), and ×/Esc/scroll-away still work.
const SCROLL_SETTLE = 250; // ms of scroll silence before "resting" opens
const pointer = { x: 0, y: 0, valid: false };
let lastScrollAt = -Infinity;
let settleTimer = 0;
const settleListeners = new Set();
let trackerInstalled = false;

const isScrolling = () => performance.now() - lastScrollAt < SCROLL_SETTLE;

function installScrollHoverTracker() {
  if (trackerInstalled) return;
  trackerInstalled = true;
  // mousemove too (not just pointermove): Chrome's synthetic post-scroll mouse
  // move carries coordinates, so a visitor who only scrolls — never moving the
  // physical mouse — still seeds the pointer position for the settle check.
  const onMove = (event) => {
    pointer.x = event.clientX;
    pointer.y = event.clientY;
    pointer.valid = true;
  };
  window.addEventListener('pointermove', onMove, { passive: true });
  window.addEventListener('mousemove', onMove, { passive: true });
  // capture: the slideshow carousel is its own horizontal scroller — paging it
  // is also "scrolling" and must not pop reveals open mid-motion.
  document.addEventListener(
    'scroll',
    () => {
      lastScrollAt = performance.now();
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        for (const fn of settleListeners) fn();
      }, SCROLL_SETTLE);
    },
    { passive: true, capture: true }
  );
}

class SlideReveal extends HTMLElement {
  connectedCallback() {
    // --- Portal out of .slide__content, into <slideshow-slide> ---
    this.slide = this.closest('slideshow-slide');
    if (this.slide && this.parentElement !== this.slide) {
      this.slide.appendChild(this);
    }

    this.trigger = this.querySelector(TRIGGER);
    this.closeBtn = this.querySelector(CLOSE);
    this.overlay = this.querySelector('.slide-reveal__overlay');
    this.content = this.querySelector('.slide-reveal__content');
    this.isOpen = false;

    // event.detail === 0 ⇒ activated by keyboard (Enter/Space); ≥ 1 ⇒ mouse.
    // We only move focus for keyboard users, so a mouse click never paints the
    // focus-visible ring (which programmatic .focus() otherwise triggers).
    this._onTrigger = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.open(event.detail === 0);
    };
    this._onClose = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.close(event.detail === 0);
    };
    // Swallow clicks so they don't bubble to slideshow-slide's slide-select.
    this._onClick = (event) => event.stopPropagation();
    // Swallow the pointer/touch/mouse DOWN events on the controls so the
    // slideshow's drag gesture (slideshow.js #handleMouseDown, bound on the
    // scroller) never engages — otherwise pressing a button starts a drag and
    // the slide nudges + snaps back before the panel opens/closes. We only
    // stopPropagation (not preventDefault), so the buttons' own click is intact.
    this._onDown = (event) => event.stopPropagation();
    this._downEvents = ['pointerdown', 'mousedown', 'touchstart'];
    this._onKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close(true); // keyboard close → return focus to the trigger
      }
    };

    this.trigger?.addEventListener('click', this._onTrigger);
    this.closeBtn?.addEventListener('click', this._onClose);
    this.addEventListener('click', this._onClick);
    for (const type of this._downEvents) {
      this.trigger?.addEventListener(type, this._onDown);
      this.closeBtn?.addEventListener(type, this._onDown);
    }

    // --- Scroll-away close: when the slide leaves the viewport, close. ---
    if ('IntersectionObserver' in window && this.slide) {
      this._observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting && this.isOpen) this.close();
          }
        },
        { threshold: 0.2 }
      );
      this._observer.observe(this.slide);
    }

    // --- Desktop hover trigger (opt-in) ---
    // When enabled, the slide itself is the trigger on real mouse pointers at
    // desktop width: pointer over the slide opens, leaving closes. The button is
    // hidden via CSS in this mode (one trigger, not two). Touch/mobile is
    // unaffected (matchMedia won't match), and we skip the editor so authoring
    // still relies on block-select (mouse drifting wouldn't fight the selection).
    const inEditor = window.Shopify && window.Shopify.designMode;
    if (this.dataset.hoverDesktop === 'true' && this.slide && !inEditor) {
      this._hoverMq = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
      // Hover intent + grace: debounce enter/leave so passing the cursor over
      // the slide (or briefly dipping out) doesn't snap-open/close. Combined with
      // the reversible timeline (#play/#reverse), the motion stays smooth even
      // when the pointer changes its mind mid-animation. Delays are editor-set
      // (per block), falling back to the module defaults if unset.
      const parseDelay = (v, fallback) => {
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n >= 0 ? n : fallback;
      };
      const openDelay = parseDelay(this.dataset.hoverOpenDelay, HOVER_OPEN_DELAY);
      const closeDelay = parseDelay(this.dataset.hoverCloseDelay, HOVER_CLOSE_DELAY);
      // The panel OPENS when the pointer enters the slide, but it CLOSES only when
      // the pointer leaves the whole slideshow — not just this slide. The carousel
      // arrows stay on top of the open panel (see _slide-reveal.liquid) so visitors
      // can keep paging while it's up, but those arrows are DOM siblings of the
      // slide (they live in <slideshow-container>, not inside <slideshow-slide>).
      // If we closed on the slide's own mouseleave, reaching for an arrow — which
      // sits over the slide but outside its subtree — would dismiss the panel. The
      // slideshow contains both the slide and the arrows, so leaving IT is the true
      // "pointer is done here" signal. Falls back to the slide if (somehow) detached.
      this._hoverSurface = this.slide.closest('slideshow-component') || this.slide;
      // The scheduled open re-checks the gate when it fires: if scrolling
      // resumed inside the delay window, skip — the next settle re-evaluates.
      this._scheduleOpen = () => {
        clearTimeout(this._hoverOpenTimer);
        this._hoverOpenTimer = setTimeout(() => {
          if (!isScrolling()) this.open(false);
        }, openDelay);
      };
      this._onSlideEnter = () => {
        if (!this._hoverMq.matches) return;
        clearTimeout(this._hoverCloseTimer);
        // Scroll-gated (D77): enters fired by sections sliding under a parked
        // cursor are ignored; the settle listener below handles resting opens.
        if (isScrolling()) return;
        this._scheduleOpen();
      };
      this._onHoverLeave = () => {
        if (!this._hoverMq.matches) return;
        clearTimeout(this._hoverOpenTimer);
        this._hoverCloseTimer = setTimeout(() => this.close(false), closeDelay);
      };
      // D77 — when scrolling settles, open if the cursor is resting on this
      // slide. elementFromPoint on tracked coordinates is deterministic where
      // Chrome's scroll-induced hover events are not (they skip entirely under
      // momentum/programmatic scrolling — the old "first scroll shows nothing").
      this._onScrollSettle = () => {
        if (!this._hoverMq.matches || this.isOpen || !pointer.valid) return;
        const under = document.elementFromPoint(pointer.x, pointer.y);
        if (!under || !this.slide.contains(under)) return;
        clearTimeout(this._hoverCloseTimer);
        this._scheduleOpen();
      };
      // If the viewport crosses below desktop while a hover-opened panel is up,
      // close it so it doesn't get stuck open with no button to dismiss it.
      this._onHoverMqChange = () => { if (!this._hoverMq.matches && this.isOpen) this.close(); };
      installScrollHoverTracker();
      settleListeners.add(this._onScrollSettle);
      this.slide.addEventListener('mouseenter', this._onSlideEnter);
      this._hoverSurface.addEventListener('mouseleave', this._onHoverLeave);
      this._hoverMq.addEventListener('change', this._onHoverMqChange);
    }

    // --- Mobile tap trigger (opt-in) ---
    // When enabled, tapping the slide opens the panel (the button is hidden via
    // CSS and the host is the full-slide tap surface while closed). The tap is
    // caught here, inside the block, so the existing click-swallow stops the
    // slide's own /select navigation; the down-swallow stops a drag-nudge. The ×
    // stays as the close (no "leave" on touch). Skipped in the editor (authoring
    // opens via block-select). matchMedia matches the CSS 749px breakpoint.
    if (this.dataset.tapMobile === 'true' && !inEditor) {
      this._tapMq = window.matchMedia('(max-width: 749px)');
      this._onTapOpen = (event) => {
        if (!this._tapMq.matches || this.isOpen) return;
        // A tap on the × (open→close) bubbles here too; don't let it re-open.
        if (this.closeBtn?.contains(event.target)) return;
        this.open(false); // pointer open → no focus move
      };
      this.addEventListener('click', this._onTapOpen);
      // Swallow the press on the slide surface so the slideshow's mouse-drag
      // (slideshow.js #handleMouseDown) never nudges the carousel on tap. Only
      // stopPropagation — native touch scroll-snap (swipe) is unaffected.
      for (const type of this._downEvents) this.addEventListener(type, this._onDown);
    }

    // --- Theme editor: open when this block (or a nested block) is selected. ---
    if (window.Shopify && window.Shopify.designMode) {
      this._onSelect = (event) => {
        if (this.contains(event.target)) this.open();
      };
      this._onDeselect = (event) => {
        if (this.contains(event.target)) this.close();
      };
      document.addEventListener('shopify:block:select', this._onSelect);
      document.addEventListener('shopify:block:deselect', this._onDeselect);
    }

    // D77 — warm the timeline ahead of the first open. Building it lazily on
    // first hover added a getComputedStyle + timeline-construction hitch to the
    // very first reveal of each section ("the first time takes longer").
    this.#warmTimeline();
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this._onTrigger);
    this.closeBtn?.removeEventListener('click', this._onClose);
    this.removeEventListener('click', this._onClick);
    for (const type of this._downEvents || []) {
      this.trigger?.removeEventListener(type, this._onDown);
      this.closeBtn?.removeEventListener(type, this._onDown);
    }
    document.removeEventListener('keydown', this._onKeydown);
    if (this._onSlideEnter) {
      this.slide?.removeEventListener('mouseenter', this._onSlideEnter);
      this._hoverSurface?.removeEventListener('mouseleave', this._onHoverLeave);
      this._hoverMq?.removeEventListener('change', this._onHoverMqChange);
      settleListeners.delete(this._onScrollSettle);
      clearTimeout(this._hoverOpenTimer);
      clearTimeout(this._hoverCloseTimer);
    }
    clearTimeout(this._warmTimer);
    this._tl?.kill();
    if (this._onTapOpen) {
      this.removeEventListener('click', this._onTapOpen);
      for (const type of this._downEvents || []) this.removeEventListener(type, this._onDown);
    }
    this._observer?.disconnect();
    if (this._onSelect) {
      document.removeEventListener('shopify:block:select', this._onSelect);
      document.removeEventListener('shopify:block:deselect', this._onDeselect);
    }
  }

  open(moveFocus = false) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.setAttribute('open', ''); // state only: hides arrows, enables pointer-events
    this.trigger?.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this._onKeydown);
    this.#play();
    // Move focus to the close control ONLY for keyboard users (skip in the
    // editor to avoid scroll jank while authoring). Mouse/touch opens leave
    // focus alone so no focus-visible ring is painted.
    if (moveFocus && !(window.Shopify && window.Shopify.designMode)) {
      this.closeBtn?.focus({ preventScroll: true });
    }
  }

  close(moveFocus = false) {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.trigger?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this._onKeydown);
    this.#reverse(); // reverses the timeline; drops `open` once fully closed
    // Return focus to the trigger only for keyboard closes (Esc / Enter) — a
    // mouse click on × leaves focus alone, so no focus-visible ring appears.
    if (moveFocus && !(window.Shopify && window.Shopify.designMode)) {
      this.trigger?.focus({ preventScroll: true });
    }
  }

  // ── Motion (GSAP) — ONE reversible timeline ──────────────────────────────
  // Best practice for re-triggerable (hover) reveals: build the open timeline
  // ONCE and play()/reverse() it. Interrupting mid-motion then eases from the
  // current frame instead of snapping back to the start (the old fromTo rebuild
  // did snap — that was the "rough" hover). GSAP owns the animated props (overlay
  // clip-path + fade, content fade + travel, trigger/close fade); blur, position,
  // colors and pointer-events stay in CSS. Shapes/duration come from the editor.
  #readMotion() {
    const cs = getComputedStyle(this.overlay);
    // Fallbacks use explicit % units to match the CSS — GSAP only interpolates
    // clip-path when both ends share identical units/structure (else it snaps).
    this._clipClosed = cs.getPropertyValue('--sr-clip-closed').trim() || 'inset(0% 0% 0% 0%)';
    this._clipOpen = cs.getPropertyValue('--sr-clip-open').trim() || 'inset(0% 0% 0% 0%)';
    const durMs = parseFloat(getComputedStyle(this).getPropertyValue('--sr-duration')) || 500;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._dur = reduce ? 0 : durMs / 1000;
    // Slide-in: content travels in from the chosen edge (matches the prior CSS 48px).
    const off = { up: { x: 0, y: 48 }, down: { x: 0, y: -48 }, left: { x: 48, y: 0 }, right: { x: -48, y: 0 } };
    this._offset = this.dataset.reveal === 'slide-in' ? (off[this.dataset.direction] || { x: 0, y: 0 }) : { x: 0, y: 0 };
  }

  // Build the paused open timeline (closed → open). Reverse plays it back to
  // closed and drops the `open` attribute on completion. Eases (power2.inOut)
  // read symmetrically in both directions.
  #buildTimeline() {
    const gsap = window.gsap;
    // D76 — a kill() mid-reverse silently swallows onReverseComplete, which is
    // the ONLY place the `open` attribute is dropped: the invisible overlay
    // would keep pointer-events:auto and eat every click on the slide. If we
    // are rebuilding while a close is mid-flight, finish the close's cleanup
    // here before killing the old timeline.
    if (this._tl && !this.isOpen && this.hasAttribute('open')) {
      gsap?.set(this.overlay, { visibility: 'hidden' });
      this.removeAttribute('open');
    }
    this._tl?.kill();
    this._tl = null;
    if (!gsap || !this.overlay) return null;
    this.#readMotion();
    const ease = 'power2.inOut';
    const d = this._dur;
    const tl = gsap.timeline({
      paused: true,
      onReverseComplete: () => {
        // Re-hide once fully closed. We pin the overlay's opacity at 1 (see
        // below), so visibility is what actually hides the blur between reveals.
        gsap.set(this.overlay, { visibility: 'hidden' });
        this.removeAttribute('open');
      },
    });
    // Overlay reveal. Two modes:
    //  • 'fade'  — tween the overlay's opacity (the frosted layer gently appears).
    //  • others  — clip-path ONLY, opacity pinned at 1.
    // For the clip styles we deliberately never tween opacity: fading a
    // backdrop-filter layer makes Chromium re-rasterize the blur every frame
    // (the "blink"), and the clip-path already does the reveal. 'fade' is the
    // intentional exception — there's no clip motion to carry it, so opacity IS
    // the reveal; it's smooth because the layer is now a stable promoted
    // compositor layer (no create/destroy churn — see _slide-reveal.liquid).
    // (The content group has no backdrop-filter, so its own fade always stays.)
    if (this.dataset.reveal === 'fade') {
      tl.set(this.overlay, { clipPath: 'none' }, 0)
        .fromTo(this.overlay,
          { autoAlpha: 0 },
          { autoAlpha: 1, duration: Math.max(d, 0.001), ease }, 0);
    } else {
      tl.set(this.overlay, { autoAlpha: 1 }, 0)
        .fromTo(this.overlay,
          { clipPath: this._clipClosed },
          { clipPath: this._clipOpen, duration: Math.max(d, 0.001), ease }, 0);
    }
    tl.to(this.trigger, { autoAlpha: 0, duration: Math.max(d * 0.4, 0.001) }, 0)
      .fromTo(this.content,
        { autoAlpha: 0, xPercent: -50, yPercent: -50, x: this._offset.x, y: this._offset.y },
        { autoAlpha: 1, x: 0, y: 0, duration: Math.max(d, 0.001), ease }, d * 0.15)
      .to(this.closeBtn, { autoAlpha: 1, duration: Math.max(d * 0.5, 0.001) }, d * 0.45);
    this._tl = tl;
    return tl;
  }

  // D77 — pre-build the paused timeline at idle so the first open plays
  // immediately. GSAP loads from its own script tag and may land after this
  // module executes, so retry briefly; past the cap, the first open simply
  // builds it lazily as before.
  #warmTimeline() {
    this._warmTries = 20; // × 250ms ≈ 5s of patience for gsap.min.js
    const attempt = () => {
      if (this._tl || this.isOpen || !this.isConnected) return;
      if (window.gsap) {
        this.#buildTimeline();
        return;
      }
      if (this._warmTries-- > 0) this._warmTimer = setTimeout(attempt, 250);
    };
    attempt();
  }

  #play() {
    const gsap = window.gsap;
    if (!gsap || !this.overlay) return; // graceful no-op (CSS keeps it hidden without GSAP)
    // From a fully-closed start, rebuild only in the theme editor — that's the
    // one place settings change underneath us, and always rebuilding would
    // throw away the D77 pre-warmed timeline on every first open. Mid-flight,
    // reuse the timeline so an interrupted close eases into re-open.
    if (!this._tl || (window.Shopify?.designMode && this._tl.progress() === 0)) {
      this.#buildTimeline();
    }
    this._tl?.play();
  }

  #reverse() {
    // D76 — reverse() on a timeline already at progress 0 is a no-op in GSAP:
    // onReverseComplete never fires, so the cleanup it carries (re-hide the
    // overlay, drop `open`) never runs and the panel state sticks (seen with
    // rapid same-tick open/close thrash). Clean up synchronously in that case.
    if (!this._tl || this._tl.progress() === 0) {
      window.gsap?.set(this.overlay, { visibility: 'hidden' });
      this.removeAttribute('open');
      this._tl?.pause(0);
      return;
    }
    this._tl.reverse();
  }
}

if (!customElements.get('slide-reveal')) {
  customElements.define('slide-reveal', SlideReveal);
}
